import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { VerdictBadge, TrafficLight } from "./VerdictBadge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
    ExternalLink, Share2, Bookmark, BookmarkCheck, Copy, Download,
    AlertOctagon, LifeBuoy, ShieldCheck, Tag, Twitter, Facebook, Linkedin, Link as LinkIcon, X as XIcon, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Comments from "./Comments";
import { ConsensusPanel } from "./ConsensusPanel";
import { Link } from "react-router-dom";

const credColor = {
    high: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
    medium: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    low: "text-red-300 border-red-500/40 bg-red-500/10",
};

export function FactCheckResult({ result, onSaved }) {
    const { user } = useAuth();
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(!!result?.saved);
    const [downloading, setDownloading] = useState(false);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const cardRef = useRef(null);

    if (!result) return null;

    const isScam = result.mode === "scam";

    const handleSave = async () => {
        if (!user) { toast.error("Sign in to save fact-checks"); return; }
        if (!user.is_pro) { toast.error("Saving is a Pro feature"); return; }
        setSaving(true);
        try {
            if (saved) {
                await api.post(`/factcheck/${result.check_id}/unsave`);
                setSaved(false);
                toast.success("Removed from saved");
            } else {
                await api.post(`/factcheck/${result.check_id}/save`);
                setSaved(true);
                toast.success("Saved to your library");
            }
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const shareUrl = `${window.location.origin}/api/share/${result.check_id}`;
    const shareText = `Real-Check verdict: ${result.verdict.replace(/_/g, " ").toUpperCase()} (${result.confidence}%) — "${(result.claim || "").slice(0, 140)}"`;

    const handleShare = async () => {
        // Try Web Share API with image file (mobile). Fall back to menu.
        if (navigator.share) {
            try {
                // Try to attach the PNG card as a share file
                if (navigator.canShare) {
                    try {
                        const res = await fetch(`/api/share/card/${result.check_id}.png`);
                        const blob = await res.blob();
                        const file = new File([blob], `realcheck-${result.check_id}.png`, { type: "image/png" });
                        if (navigator.canShare({ files: [file] })) {
                            await navigator.share({ title: "Real-Check verdict", text: shareText, url: shareUrl, files: [file] });
                            return;
                        }
                    } catch { /* fall through */ }
                }
                await navigator.share({ title: "Real-Check verdict", text: shareText, url: shareUrl });
                return;
            } catch (e) {
                if (e?.name === "AbortError") return;
            }
        }
        // Desktop / no Web Share → open the platform menu
        setShowShareMenu(true);
    };

    const copyShareLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Share link copied");
        } catch {
            toast.error("Could not copy");
        }
    };

    const copyText = async () => {
        const label = isScam ? "Scam check" : "Fact-check";
        const text = `Real-Check ${label}: ${result.verdict.replace(/_/g, " ").toUpperCase()} (${result.confidence}%)\n\n"${result.claim}"\n\n${result.summary}\n\nvia real-check.org`;
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
    };

    const downloadCard = async () => {
        setDownloading(true);
        try {
            const res = await fetch(`/api/share/card/${result.check_id}.png`);
            if (!res.ok) throw new Error("card fetch failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.download = `realcheck-${result.check_id}.png`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
            toast.success("Truth card downloaded");
        } catch {
            toast.error("Could not export image");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            data-testid="factcheck-result"
        >
            {/* Cinematic reveal flash */}
            <motion.div
                initial={{ opacity: 0.8, scale: 0.98 }}
                animate={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                aria-hidden
                className="absolute pointer-events-none"
                style={{
                    inset: -20,
                    borderRadius: 32,
                    background: `radial-gradient(circle at 50% 30%, hsl(var(--primary) / 0.35) 0%, transparent 65%)`,
                }}
            />

            {/* Off-screen shareable card (used for PNG export) */}
            <ShareableCard result={result} innerRef={cardRef} />

            <Card className="card-surface p-8 lg:p-10 rounded-2xl">
                <div className="flex flex-col md:flex-row md:items-start gap-8">
                    <TrafficLight verdict={result.verdict} size={22} />
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            <motion.div
                                initial={{ scale: 0.6, opacity: 0, rotate: -8 }}
                                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                                transition={{ delay: 0.15, duration: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
                            >
                                <VerdictBadge verdict={result.verdict} confidence={result.confidence} size="lg" />
                            </motion.div>
                            {result.category && result.category !== "General" && (
                                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-white/5 text-muted-foreground border border-white/10 flex items-center gap-1" data-testid="factcheck-category">
                                    <Tag className="h-3 w-3" /> {result.category}
                                </span>
                            )}
                            {isScam && (
                                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-red-500/10 text-red-300 border border-red-500/30" data-testid="factcheck-scam-mode">
                                    Scam check
                                </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                                {new Date(result.created_at).toLocaleString()}
                            </span>
                        </div>
                        <p className="text-muted-foreground text-xs uppercase tracking-widest mb-2">
                            {isScam ? "The suspicious message" : "The claim"}
                        </p>
                        <h2 className="font-display text-2xl md:text-3xl leading-tight text-accent mb-6 whitespace-pre-wrap break-words" data-testid="factcheck-claim">
                            &ldquo;{result.claim}&rdquo;
                        </h2>

                        <p className="text-lg text-accent/95 leading-relaxed mb-4 font-medium" data-testid="factcheck-summary">
                            {result.summary}
                        </p>
                        {result.detailed_analysis && (
                            <p className="text-muted-foreground leading-relaxed text-base" data-testid="factcheck-detailed">
                                {result.detailed_analysis}
                            </p>
                        )}

                        {result.red_flags?.length > 0 && (
                            <div className="mt-8 p-5 rounded-2xl bg-red-500/5 border border-red-500/20">
                                <h3 className="flex items-center gap-2 font-display text-sm uppercase tracking-widest text-red-300 mb-3">
                                    <AlertOctagon className="h-4 w-4" /> Red flags
                                </h3>
                                <ul className="space-y-2" data-testid="factcheck-red-flags">
                                    {result.red_flags.map((f, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-accent/90">
                                            <span className="text-red-400 mt-1">▸</span> {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {result.recommended_actions?.length > 0 && (
                            <div className="mt-4 p-5 rounded-2xl bg-emerald-500/5 border border-emerald-500/20">
                                <h3 className="flex items-center gap-2 font-display text-sm uppercase tracking-widest text-emerald-300 mb-3">
                                    <LifeBuoy className="h-4 w-4" /> What to do next
                                </h3>
                                <ul className="space-y-2" data-testid="factcheck-actions">
                                    {result.recommended_actions.map((a, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-accent/90">
                                            <span className="text-emerald-400 mt-1">✓</span> {a}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {result.sources?.length > 0 && (
                            <div className="mt-8">
                                <h3 className="font-display text-sm uppercase tracking-widest text-muted-foreground mb-3">
                                    Sources ({result.sources.length})
                                </h3>
                                <ul className="space-y-2" data-testid="factcheck-sources-list">
                                    {result.sources.map((s, i) => (
                                        <li key={i}>
                                            <a
                                                href={s.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`source-link-${i}`}
                                                className="group flex items-start gap-3 p-3 rounded-lg border border-white/5 hover:border-primary/40 hover:bg-white/[0.03] transition-colors"
                                            >
                                                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${credColor[s.credibility] || credColor.medium} whitespace-nowrap mt-0.5`}>
                                                    {s.credibility}
                                                </span>
                                                <span className="flex-1 text-sm text-accent/90 group-hover:text-accent leading-snug">
                                                    {s.title}
                                                </span>
                                                <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary mt-0.5" />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Button
                                onClick={handleShare}
                                data-testid="factcheck-share-button"
                                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25"
                            >
                                <Share2 className="h-4 w-4 mr-2" /> Share the Truth
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={downloadCard}
                                disabled={downloading}
                                data-testid="factcheck-download-card-button"
                                className="rounded-full"
                            >
                                <Download className="h-4 w-4 mr-2" /> {downloading ? "Rendering…" : "Download card"}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={copyText}
                                data-testid="factcheck-copy-button"
                                className="rounded-full"
                            >
                                <Copy className="h-4 w-4 mr-2" /> Copy
                            </Button>
                            <Button
                                variant={saved ? "default" : "outline"}
                                onClick={handleSave}
                                disabled={saving}
                                data-testid="factcheck-save-button"
                                className="rounded-full"
                            >
                                {saved ? <BookmarkCheck className="h-4 w-4 mr-2" /> : <Bookmark className="h-4 w-4 mr-2" />}
                                {saved ? "Saved" : "Save"}
                            </Button>
                            {!user?.is_pro && (
                                <Link
                                    to="/pricing"
                                    className="text-xs text-primary hover:underline ml-auto"
                                    data-testid="factcheck-upgrade-hint"
                                >
                                    <ShieldCheck className="h-3 w-3 inline mr-1" />
                                    Upgrade to save & get unlimited checks →
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
                <div className="mt-6">
                    <ConsensusPanel claim={result.claim} />
                </div>
                <Comments checkId={result.check_id} />
            </Card>
            {/* Platform share menu (desktop / fallback when Web Share unavailable) */}
            {showShareMenu && (
                <ShareMenu
                    shareUrl={shareUrl}
                    shareText={shareText}
                    imageUrl={`${window.location.origin}/api/share/card/${result.check_id}.png`}
                    onClose={() => setShowShareMenu(false)}
                    onCopy={copyShareLink}
                    onDownload={downloadCard}
                />
            )}
        </motion.div>
    );
}


function ShareMenu({ shareUrl, shareText, imageUrl, onClose, onCopy, onDownload }) {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    const targets = [
        { key: "twitter",  label: "X / Twitter", icon: Twitter,        color: "#1DA1F2",
          href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}` },
        { key: "facebook", label: "Facebook",    icon: Facebook,       color: "#1877F2",
          href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
        { key: "linkedin", label: "LinkedIn",    icon: Linkedin,       color: "#0A66C2",
          href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
        { key: "whatsapp", label: "WhatsApp",    icon: MessageCircle,  color: "#25D366",
          href: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}` },
    ];
    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="share-menu"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="card-surface rounded-2xl p-6 w-full max-w-md border border-primary/20"
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-2xl text-accent">Share the Truth</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-accent" data-testid="share-menu-close">
                        <XIcon className="h-5 w-5" />
                    </button>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    A branded verdict card will preview on every platform below.
                </p>
                <div className="rounded-xl overflow-hidden border border-white/10 mb-5 bg-black/50">
                    <img
                        src={imageUrl}
                        alt="Verdict card preview"
                        className="w-full h-auto block"
                        loading="lazy"
                        data-testid="share-menu-preview"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                    {targets.map((t) => {
                        const Icon = t.icon;
                        return (
                            <a
                                key={t.key}
                                href={t.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setTimeout(onClose, 400)}
                                className="flex items-center gap-3 rounded-xl border border-white/10 px-4 py-3 hover:border-primary/50 hover:bg-white/5 transition-colors"
                                data-testid={`share-menu-${t.key}`}
                                style={{ color: t.color }}
                            >
                                <Icon className="h-5 w-5" />
                                <span className="text-sm font-medium text-accent">{t.label}</span>
                            </a>
                        );
                    })}
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Button variant="secondary" onClick={onCopy} className="rounded-full" data-testid="share-menu-copy">
                        <LinkIcon className="h-4 w-4 mr-2" /> Copy link
                    </Button>
                    <Button variant="secondary" onClick={onDownload} className="rounded-full" data-testid="share-menu-download">
                        <Download className="h-4 w-4 mr-2" /> Save image
                    </Button>
                </div>
            </motion.div>
        </div>
    );
}

/**
 * ShareableCard is the printable 1200x1500 branded card exported to PNG.
 * Positioned off-screen but rendered so html-to-image can capture it.
 */
function ShareableCard({ result, innerRef }) {
    const verdictLabel = result.verdict.replace(/_/g, " ").toUpperCase();
    const tone = {
        true: { bg: "#0d3d2a", ring: "#10B981", text: "#10B981" },
        mostly_true: { bg: "#0d3d2a", ring: "#10B981", text: "#10B981" },
        safe: { bg: "#0d3d2a", ring: "#10B981", text: "#10B981" },
        likely_safe: { bg: "#0d3d2a", ring: "#10B981", text: "#10B981" },
        misleading: { bg: "#3b2a08", ring: "#F59E0B", text: "#F59E0B" },
        suspicious: { bg: "#3b2a08", ring: "#F59E0B", text: "#F59E0B" },
        false: { bg: "#3b0e0e", ring: "#EF4444", text: "#EF4444" },
        scam: { bg: "#3b0e0e", ring: "#EF4444", text: "#EF4444" },
        unverified: { bg: "#1a1e40", ring: "#8890c9", text: "#c5c8e6" },
    }[result.verdict] || { bg: "#1a1e40", ring: "#8890c9", text: "#c5c8e6" };

    return (
        <div style={{ position: "absolute", left: "-99999px", top: 0 }}>
            <div
                ref={innerRef}
                style={{
                    width: 1200,
                    minHeight: 1500,
                    background: "linear-gradient(180deg, #050826 0%, #0a0f3d 100%)",
                    padding: 80,
                    fontFamily: "'Outfit','IBM Plex Sans',sans-serif",
                    color: "#FFF6D6",
                    boxSizing: "border-box",
                    position: "relative",
                }}
            >
                {/* Logo */}
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 64 }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: "50%",
                        background: "linear-gradient(135deg, #7C6BFF, #3D2CE0)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 32, color: "#FFF6D6", fontWeight: 900,
                    }}>◈</div>
                    <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: -1.5 }}>
                        Real<span style={{ color: "#7C6BFF" }}>Check</span>
                        <span style={{ fontSize: 20, color: "#8890c9", marginLeft: 10 }}>.org</span>
                    </div>
                </div>
                {/* Verdict badge */}
                <div style={{
                    display: "inline-block",
                    padding: "14px 28px",
                    borderRadius: 9999,
                    background: tone.bg,
                    border: `2px solid ${tone.ring}`,
                    color: tone.text,
                    fontWeight: 900,
                    fontSize: 32,
                    letterSpacing: 3,
                    marginBottom: 40,
                }}>
                    {verdictLabel} · {result.confidence}%
                </div>
                {/* Category */}
                {result.category && result.category !== "General" && (
                    <div style={{ fontSize: 18, color: "#8890c9", textTransform: "uppercase", letterSpacing: 3, marginBottom: 20 }}>
                        {result.mode === "scam" ? "Scam check" : "Fact-check"} · {result.category}
                    </div>
                )}
                {/* Claim */}
                <div style={{ fontSize: 20, color: "#8890c9", textTransform: "uppercase", letterSpacing: 3, marginBottom: 16 }}>
                    {result.mode === "scam" ? "The message" : "The claim"}
                </div>
                <div style={{
                    fontSize: 44, lineHeight: 1.2, fontWeight: 700, color: "#FFF6D6",
                    marginBottom: 44, letterSpacing: -1,
                    display: "-webkit-box",
                    WebkitLineClamp: 4, WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                }}>
                    &ldquo;{result.claim}&rdquo;
                </div>
                {/* Summary */}
                <div style={{ fontSize: 24, lineHeight: 1.5, color: "#c5c8e6", marginBottom: 64 }}>
                    {result.summary}
                </div>
                {/* Sources */}
                {result.sources?.length > 0 && (
                    <div style={{ marginBottom: 40 }}>
                        <div style={{ fontSize: 16, color: "#8890c9", textTransform: "uppercase", letterSpacing: 3, marginBottom: 16 }}>
                            Sources
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                            {result.sources.slice(0, 4).map((s, i) => (
                                <div key={i} style={{
                                    padding: "10px 18px",
                                    borderRadius: 9999,
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    fontSize: 16, color: "#c5c8e6",
                                    maxWidth: 500,
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                }}>
                                    {s.title}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* Footer */}
                <div style={{
                    position: "absolute",
                    bottom: 60, left: 80, right: 80,
                    borderTop: "1px solid rgba(255,255,255,0.1)",
                    paddingTop: 24,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: 18, color: "#8890c9",
                }}>
                    <div>Verified by Real-Check · real-check.org</div>
                    <div style={{ color: "#7C6BFF", fontWeight: 700 }}>♥ Funds Hands of Hope</div>
                </div>
            </div>
        </div>
    );
}
